import React, { useEffect, useRef, useState } from 'react';

import { Button, Input, Layout, Space, Table, notification } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { ColumnType } from 'antd/es/table';
import {
    SearchOutlined,
} from "@ant-design/icons";
import { connect, useDispatch } from 'react-redux';
import { acceptRejectApproval, getInventoryApprovals } from '../../../redux/actions/API/inventoryApprovalAction';
import Loader from '../../../Home/Loader/Loader';
import { getUsers } from '../../../redux/actions/API/users';
import { useNavigate } from 'react-router-dom';

interface InventoryApprovalProps {
    companyId: string;
    approvalData: any;
    approvalTotal: number;
    getInventoryApprovals: Function;
    approvalLoading: boolean;
    getUsers: Function;
    userData: any;
    loginData: any;
    acceptRejectApproval: Function;
    acceptRejectApprovalMessage: string;
    userId: string;
}

const InventoryApproval: React.FC<InventoryApprovalProps> = ({
    approvalData, approvalTotal, companyId, getInventoryApprovals,
    approvalLoading, getUsers, userData, loginData,
    acceptRejectApproval, acceptRejectApprovalMessage, userId
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [usersMap, setUsersMap] = useState<any>({});
    const [tableFilters, setTableFilters] = useState<any>({});
    const [view, setView] = useState(false);
    const [edit, setEdit] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const options: any = {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
    };

    const getColumnSearchProps = (dataIndex: string): ColumnType<any> => {
        const searchInput = useRef<any>(null);
        return {
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={searchInput}
                        placeholder={`Search ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) =>
                            setSelectedKeys(e.target.value ? [e.target.value] : [])
                        }
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                clearFilters?.();
                                confirm();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
            ),
        };
    };

    const columns: any = [
        {
            title: "Approval ID",
            dataIndex: "approvalId",
            width: "180px",
            ...getColumnSearchProps("approvalId"),
            render: (text: string, record: { id: number }) => {
                return <span onClick={() => {
                    navigate(`/inventoryPreview?approvalId=${record?.id}`)
                }} className='link'>{text}</span>
            }
        },
        {
            title: "Document Type",
            dataIndex: "documentType",
            width: "180px",
            ...getColumnSearchProps("documentType"),
        },
        {
            title: "Document Number",
            dataIndex: "documentNumber",
            width: "180px",
            ...getColumnSearchProps("documentNumber"),
            render: (text: string, record: any) => {
                if (!text) return '';
                if (['Scrap Material', 'Raw Material', 'Finished Good', 'Raw Material Return',
                    'Production Discarded - Raw Material', 'Production Discarded - Return Raw Material',
                    'Production Discarded - Scrap Material', 'Production Discarded - Finished Good'
                ]?.includes(record?.documentType)) {
                    return record?.productionId ? <span className='link' onClick={() => {
                        record?.bulkProductionId ? navigate(
                            `/view-bulk-production?productionId=${record?.bulkProductionId}`
                        ) : navigate(
                            `/view_production?productionId=${text}`
                        );
                    }}>{record?.bulkProductionNumber || record?.productionId}</span> : <></>
                }
                else return <span className='link' onClick={() => {
                    navigate(
                        `/previewDocument?documentNumber=${text}`
                    );
                }}>{text}</span>
            }
        },
        {
            title: "Approval Status",
            dataIndex: "approvalStatus",
            width: "180px",
            ...getColumnSearchProps("approvalStatus"),
        },
        {
            title: "Requested Date/Time",
            dataIndex: "createdAt",
            width: "180px",
            ...getColumnSearchProps("createdAt"),
            render: (text: string) => {
                if (!text) return "-";
                const date = new Date(text);

                return date.toLocaleDateString("en-GB", options);
            },
        },
        {
            title: "Requested By",
            dataIndex: "requestedBy",
            width: "180px",
            ...getColumnSearchProps("requestedBy"),
            render: (text: string) => {
                return usersMap[text] || 'Admin'
            }
        },
        {
            title: "Action",
            key: "action",
            width: "180px",
            render: (_: any, record: any) => {
                if (record.approvalStatus !== "Pending") return null;
                if (!(loginData?.data?.role == 1 || loginData?.data?.role == 2 || edit)) return null;
                return (
                    <Space size="middle">
                        <Button type="link" onClick={() => handleApproveReject(record, true)}>
                            Approve
                        </Button>
                        <Button type="link" danger onClick={() => handleApproveReject(record, false)}>
                            Reject
                        </Button>
                    </Space>
                );
            }
        }
    ];

    useEffect(() => {
        getUsers({ companyId: Number(companyId), admin: true });
    }, [companyId]);

    useEffect(() => {
        if (companyId) {
            getInventoryApprovals({
                companyId: Number(companyId),
                pageSize,
                currentPage,
                filters: tableFilters
            });
        }
    }, [companyId, pageSize, currentPage, tableFilters]);

    useEffect(() => {
        if (Array.isArray(userData)) {
            const obj: any = userData.reduce((acc: any, curr: any) => {
                acc[curr.id] = curr.username;
                return acc;
            }, {});
            setUsersMap(obj);
        }
    }, [userData]);

    useEffect(() => {
        if (loginData?.data?.role == 1 || loginData?.data?.role == 2) {
            setView(true);
            setEdit(true);
        }
        else {
            const subfeature = loginData?.data?.permissions?.find((data: any) => data?.feature == "Inventory" && data?.subfeature == "Inventory Approval");
            if (subfeature) {
                if (subfeature?.view) {
                    setView(true);
                }
                if (subfeature?.edit) {
                    setEdit(true);
                }
            }
        }
    }, [loginData]);

    useEffect(() => {
        if (acceptRejectApprovalMessage) {
            notification.open({ type: 'success', message: acceptRejectApprovalMessage, duration: 3 });
            getInventoryApprovals({
                companyId: Number(companyId),
                pageSize,
                currentPage,
                filters: tableFilters
            });
            dispatch({
                type: 'ACCEPT_REJECT_APPROVAL_SUCCESS', payload: {
                    responseData: {
                        message: ''
                    }
                }
            });
        }
    }, [acceptRejectApprovalMessage]);

    const handleApproveReject = (record: any, isApproved: boolean) => {
        acceptRejectApproval({
            approvalId: record.id,
            approvedBy: Number(userId),
            isApproved,
            items: [],
            by: loginData?.data?.name,
            comment: ''
        });
    };

    return (
        <Layout className="layout">
            <Loader loading={approvalLoading} />
            <Content className="content-section">
                {(loginData?.data?.role == 1 || loginData?.data?.role == 2 || view) ? <>
                    <div style={{ fontWeight: "500", fontSize: "16px" }}>
                        Inventory Approval
                    </div>
                    <Table
                        bordered
                        columns={columns}
                        dataSource={Array.isArray(approvalData) ? approvalData : []}
                        rowKey={(record: any, index) => record?.id}
                        scroll={{ x: "100%", y: 'calc(100vh - 280px)' }}
                        style={{ textAlign: "center", marginTop: 10 }}
                        pagination={{
                            position: ["bottomLeft"],
                            current: currentPage,
                            pageSize: pageSize,
                            total: approvalTotal,
                            showSizeChanger: true,
                        }}
                        onChange={(pagination, filters, sorter) => {
                            setCurrentPage(pagination.current || 1);
                            setPageSize(pagination.pageSize || 20);
                            setTableFilters(filters);
                        }}
                        className="custom-pagination-table"
                    />
                </> : <div style={{ textAlign: 'center' }}>You do not have Permission</div>}
            </Content>
        </Layout>
    )
}

const mapStateToProps = (state: any) => ({
    companyId: state.api.login?.data?.companyId,
    approvalData: state.api.getInventoryApprovals.data,
    approvalTotal: state.api.getInventoryApprovals.total || 0,
    approvalLoading: state.api.getInventoryApprovals.loading || state.api.acceptRejectApproval?.loading,
    acceptRejectApprovalMessage: state.api.acceptRejectApproval?.data,
    userData: state.api.getUsers?.data,
    loginData: state.api.login,
    userId: state.api.login?.data?.id,
});

const mapDispatchToProps = (dispatch: any) => ({
    getInventoryApprovals: (payload: any) => dispatch(getInventoryApprovals(payload)),
    getUsers: (payload: any) => dispatch(getUsers(payload)),
    acceptRejectApproval: (payload: any) => dispatch(acceptRejectApproval(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InventoryApproval);
